import { useEffect, useMemo, useState } from "react";
import { isPurchaseUser } from "../utils/permissions";
import CreatableSelect from "react-select/creatable";
import { authFetch } from "../utils/authFetch";
import * as XLSX from "xlsx";
import {
  Search,
  Package,
  Layers3,
  Tag,
  Save,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  Download,
  Edit2,
  Trash2,
  Hash,
  IndianRupee,
  Truck,
  ShieldCheck,
  UserCheck
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API = process.env.REACT_APP_API_URL;

const initialFormState = {
  material_code: "",
  department: "",
  category: "",
  sub_category: "",
  item_name: "",
  brand: [],
  thickness: "",
  uom: "",
  remarks: ""
};

function MaterialMaster() {
  const [materials, setMaterials] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [formData, setFormData] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [tableSearch, setTableSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");

  const itemsPerPage = 10;

  const t = {
    bg: "#f3f4f6",
    surface: "rgba(255,255,255,0.78)",
    card: "#ffffff",
    border: "#e5e7eb",
    borderSoft: "#f3f4f6",
    text: "#111827",
    textMuted: "#6b7280",
    textFaint: "#9ca3af",
    chip: "#111827",
    chipText: "#ffffff",
    rowHover: "#f9fafb",
    inputBg: "#ffffff",
    primary: "#111827",
    primaryText: "#ffffff",
    accent: "#E5E7EB",
    danger: "#ef4444",
  };

  useEffect(() => {
    fetchMaterials();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API}/api/master/departments`);
      const data = await res.json();
      setDepartments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchCategories = async (department) => {
    try {
      const res = await fetch(
        `${API}/api/master/categories?department=${encodeURIComponent(department)}`
      );
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchSubCategories = async (categoryId) => {
    try {
      const res = await fetch(
        `${API}/api/master/sub-categories?category_ids=${categoryId}`
      );
      const data = await res.json();
      setSubCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchMaterials = async () => {
    try {
      const res = await authFetch(`${API}/materials`);
      const data = await res.json();
      setMaterials(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet);

    const response = await authFetch(
  `${API}/materials/bulk-upload`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(rows)
      }
    );

    const result = await response.json();
    alert(result.message);
    fetchMaterials();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "department") {
        updated.category = "";
        updated.sub_category = "";
        setCategories([]);
        setSubCategories([]);
        if (value) {
          fetchCategories(value);
        }
      } else if (name === "category") {
        updated.sub_category = "";
        setSelectedCategoryId(value);
        fetchSubCategories(value);
        const selectedCat = categories.find(
          (c) => String(c.id) === String(value)
        );
        updated.category = selectedCat?.name || "";
      }

      return updated;
    });
  };

  const handleSave = async () => {
    if (!formData.material_code?.trim()) {
      alert("Material Code is required");
      return;
    }

    if (!formData.department?.trim()) {
      alert("Department is required");
      return;
    }

    if (!formData.category?.trim()) {
      alert("Category is required");
      return;
    }

    if (!formData.item_name?.trim()) {
      alert("Material Name is required");
      return;
    }

    try {
      console.log("FORM DATA:", formData);

      const url = editingId
        ? `${API}/materials/${editingId}`
        : `${API}/materials`;

      const method = editingId ? "PUT" : "POST";

      const response = await authFetch(url, {
  method,
  headers: {
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    ...formData,
    brand: Array.isArray(formData.brand)
      ? formData.brand.join(", ")
      : formData.brand
  })
});

      const data = await response.json();

      if (!response.ok) {
        alert(data.error || "Save Failed");
        return;
      }

      alert("Material Saved Successfully");
      setFormData(initialFormState);
      setEditingId(null);
      fetchMaterials();
      setShowForm(false);
    } catch (err) {
      console.error(err);
      alert("Server Error");
    }
  };

  const handleEdit = (item) => {
    const itemId = item._id || item.id;
    setEditingId(itemId);
    setFormData({
      material_code: item.material_code || "",
      department: item.department || "",
      category: item.category || "",
      sub_category: item.sub_category || "",
      item_name: item.item_name || "",
      brand: item.brand
        ? item.brand.split(",").map(x => x.trim())
        : [],
      thickness: item.thickness || "",
      uom: item.uom || "",
      remarks: item.remarks || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (item) => {
    const itemId = item._id || item.id;
    if (window.confirm(`Are you sure you want to delete "${item.item_name}"?`)) {
      try {
        await authFetch(`${API}/materials/${itemId}`, { method: "DELETE" });
        fetchMaterials();
      } catch (err) {
        console.log("Error deleting item:", err);
      }
    }
  };

  const filteredMaterials = useMemo(() => {
    return materials.filter((item) => {
      const matchesTab =
        selectedCategory === "All" ||
        item.department?.toLowerCase() === selectedCategory.toLowerCase() ||
        item.category?.toLowerCase() === selectedCategory.toLowerCase();

      const search = tableSearch.toLowerCase();
      const matchesSearch =
        item.material_code?.toLowerCase().includes(search) ||
        item.category?.toLowerCase().includes(search) ||
        item.sub_category?.toLowerCase().includes(search) ||
        item.item_name?.toLowerCase().includes(search) ||
        item.brand?.toLowerCase().includes(search) ||
        item.uom?.toLowerCase().includes(search);

      return matchesTab && matchesSearch;
    });
  }, [materials, selectedCategory, tableSearch]);

  const handleExportCSV = () => {
    if (filteredMaterials.length === 0) return alert("No data to export");

    const headers = [
      "Material Code", "Department", "Category", "Sub-Category",
      "Material Name", "Brand", "Thickness", "UOM",
    ];

    const csvRows = [
      headers.join(","),
      ...filteredMaterials.map((item) =>
        [
          `"${item.material_code || ""}"`,
          `"${item.department || ""}"`,
          `"${item.category || ""}"`,
          `"${item.sub_category || ""}"`,
          `"${item.item_name || ""}"`,
          `"${item.brand || ""}"`,
          `"${item.thickness || ""}"`,
          `"${item.uom || ""}"`
        ].join(",")
      ),
    ];

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Ensileta_Material_Master_Report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = () => {
    if (filteredMaterials.length === 0) return alert("No data to export");

    const doc = new jsPDF("l", "mm", "a4");

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Ensileta Interiors - Material Master Matrix", 14, 20);

    doc.setFont("Helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Scope Scope: ${selectedCategory} | Total Matrix Units: ${filteredMaterials.length} Items`, 14, 26);
    doc.text(`System Generated Ledger: ${new Date().toLocaleDateString()}`, 14, 31);

    const tableRows = filteredMaterials.map((item) => [
      item.material_code || "-",
      `${item.category || "-"}\n(${item.sub_category || "-"})`,
      item.item_name || "-",
      item.brand || "-",
      item.thickness || "-"
    ]);

    autoTable(doc, {
      startY: 38,
      head: [[
        "Code",
        "Category Hierarchy",
        "Material Name",
        "Brand",
        "Thickness"
      ]],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [17, 24, 39], fontSize: 8.5, fontStyle: "bold" },
      styles: { fontSize: 8, font: "Helvetica", cellPadding: 2.5 },
      alternatingRowStyles: { fillColor: [249, 250, 251] },
    });

    doc.save(`Ensileta_Material_Master_Portfolio.pdf`);
  };

  const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMaterials = filteredMaterials.slice(startIndex, startIndex + itemsPerPage);

  const s = {
    page: {
      minHeight: "100vh",
      background: t.bg,
      color: t.text,
      fontFamily: "'Inter','IBM Plex Sans',sans-serif",
      padding: "32px 24px",
    },
    wrap: { width: "100%", position: "relative" },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "24px",
    },
    title: { fontSize: "24px", fontWeight: "700", color: t.text },
    sub: { fontSize: "13px", color: t.textMuted, marginTop: "4px" },
    section: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "18px",
      overflow: "hidden",
      marginBottom: "20px",
    },
    sectionHead: {
      padding: "18px 24px",
      borderBottom: `1px solid ${t.borderSoft}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      gap: "16px",
    },
    tabContainer: {
      display: "flex",
      gap: "8px",
      padding: "0 24px",
      marginBottom: "14px",
      overflowX: "auto",
      whiteSpace: "nowrap",
    },
    tabButton: (isActive) => ({
      padding: "8px 16px",
      borderRadius: "20px",
      fontSize: "13px",
      fontWeight: "600",
      border: "none",
      cursor: "pointer",
      background: isActive ? t.primary : t.accent,
      color: isActive ? t.primaryText : t.text,
      transition: "all 0.2s ease",
    }),
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
      gap: "16px",
      padding: "24px",
    },
    input: {
      width: "100%",
      height: "40px",
      borderRadius: "10px",
      border: `1px solid ${t.border}`,
      background: t.inputBg,
      color: t.text,
      padding: "0 14px",
      fontSize: "14px",
      outline: "none",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      borderRadius: "10px",
      border: `1px solid ${t.border}`,
      background: t.inputBg,
      color: t.text,
      padding: "14px",
      fontSize: "14px",
      outline: "none",
      boxSizing: "border-box",
      resize: "none",
    },
    label: {
      display: "block",
      fontSize: "11px",
      fontWeight: "600",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: t.textMuted,
      marginBottom: "6px",
    },
    primaryBtn: {
      height: "40px",
      padding: "0 18px",
      borderRadius: "10px",
      border: "none",
      background: t.primary,
      color: t.primaryText,
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    secondaryBtn: {
      height: "40px",
      padding: "0 18px",
      borderRadius: "10px",
      border: `1px solid ${t.border}`,
      background: t.card,
      color: t.text,
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    actionBtn: (isDelete) => ({
      background: "none",
      border: "none",
      cursor: "pointer",
      padding: "6px",
      borderRadius: "6px",
      color: isDelete ? t.danger : t.textMuted,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background 0.2s",
    }),
    searchBox: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      background: t.card,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      padding: "0 14px",
      height: "44px",
      width: "320px",
    },
    searchInput: {
      flex: 1,
      border: "none",
      outline: "none",
      background: "transparent",
      color: t.text,
      fontSize: "14px",
    },
    table: { width: "100%", borderCollapse: "collapse" },
    th: {
      padding: "14px 24px",
      textAlign: "left",
      fontSize: "11px",
      fontWeight: "600",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: t.textMuted,
      borderBottom: `1px solid ${t.borderSoft}`,
      background: "rgba(250,250,250,0.6)",
    },
    td: {
      padding: "14px 24px",
      borderBottom: `1px solid ${t.borderSoft}`,
      fontSize: "14px",
    },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 12px",
      borderRadius: "999px",
      background: t.chip,
      color: t.chipText,
      fontSize: "12px",
      fontWeight: "600",
    },
  };

  return (
    <div style={s.page}>
      <div style={s.wrap}>
        {/* HEADER */}
        <div style={s.header}>
          <div>
            <div style={s.title}>Material Master</div>
          </div>

          <button
            onClick={() => {
              if (showForm) {
                setEditingId(null);
                setFormData(initialFormState);
              }
              setShowForm(!showForm);
            }}
            style={s.primaryBtn}
          >
            {showForm ? (
              <>
                <X size={15} /> Close Form Matrix
              </>
            ) : (
              <>
                <Plus size={15} /> Add Master Record
              </>
            )}
          </button>
        </div>

        {/* INPUT FORM CONTAINER */}
        {showForm && (
          <div style={s.section}>
            <div style={s.sectionHead}>
              <div>
                <div style={{ fontSize: "16px", fontWeight: "700" }}>
                  {editingId ? "Modify Material Matrix Node" : "Append Master Specification Ledger"}
                </div>
                <div style={{ fontSize: "12px", color: t.textMuted, marginTop: "4px" }}>
                  Define strategic metrics for estimation, BOQ configuration, and procurement tracking.
                </div>
              </div>

              <button onClick={handleSave} style={s.primaryBtn}>
                <Save size={15} /> {editingId ? "Update Material" : "Commit to Matrix"}
              </button>
            </div>

            <div style={s.formGrid}>
              {/* Material Code */}
              <div>
                <label style={s.label}>Material Code</label>
                <div style={{ position: "relative" }}>
                  <Hash size={16} style={{ position: "absolute", left: "12px", top: "12px", color: t.textMuted }} />
                  <input
                    type="text"
                    name="material_code"
                    value={formData.material_code}
                    onChange={handleChange}
                    placeholder="e.g. MAT-WD-PL-01"
                    style={{ ...s.input, paddingLeft: "40px" }}
                  />
                </div>
              </div>

              {/* Dynamic Organizational Cascading Dropdowns */}
              <div>
                <label style={s.label}>Department Scope</label>
                <CreatableSelect
                  isClearable
                  placeholder="Select or Create Department"
                  options={departments.map(d => ({
                    label: d.name,
                    value: d.name
                  }))}
                  value={
                    formData.department
                      ? {
                          label: formData.department,
                          value: formData.department
                        }
                      : null
                  }
                  onCreateOption={async (inputValue) => {
                    try {
                      const token = localStorage.getItem("token");
                      await fetch(`${API}/api/master/departments`, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({ name: inputValue })
                      });
                      await fetchDepartments();
                      setFormData(prev => ({ ...prev, department: inputValue }));
                    } catch (err) {
                      console.error(err);
                      alert("Failed to create department");
                    }
                  }}
                  onChange={(selected) => {
                    setFormData(prev => ({
                      ...prev,
                      department: selected?.value || "",
                      category: "",
                      sub_category: ""
                    }));
                    if (selected?.value) {
                      fetchCategories(selected.value);
                      fetch(`${API}/generate-material-code?department=${encodeURIComponent(selected.value)}`)
                        .then(res => res.json())
                        .then(data => {
                          setFormData(prev => ({
                            ...prev,
                            department: selected.value,
                            material_code: data.material_code || ""
                          }));
                        })
                        .catch(console.error);
                    }
                  }}
                />
              </div>

              <div>
                <label style={s.label}>Category Classification</label>
                <CreatableSelect
                  isClearable
                  placeholder="Select or Create Category"
                  options={categories.map(c => ({
                    label: c.name,
                    value: c.name
                  }))}
                  value={
                    formData.category
                      ? {
                          label: formData.category,
                          value: formData.category
                        }
                      : null
                  }
                  onCreateOption={async (inputValue) => {
                    try {
                      const token = localStorage.getItem("token");
                      await fetch(`${API}/api/master/categories`, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          department: formData.department,
                          name: inputValue
                        })
                      });
                      await fetchCategories(formData.department);
                      setFormData(prev => ({ ...prev, category: inputValue }));
                    } catch (err) {
                      console.error(err);
                      alert("Failed to create category");
                    }
                  }}
                  onChange={(selected) => {
                    setFormData(prev => ({
                      ...prev,
                      category: selected?.value || "",
                      sub_category: ""
                    }));
                  }}
                />
              </div>

              <div>
                <label style={s.label}>Sub-Category Core</label>
                <CreatableSelect
                  isClearable
                  placeholder="Select or Create Sub Category"
                  options={subCategories.map(sc => ({
                    label: sc.name,
                    value: sc.name
                  }))}
                  value={
                    formData.sub_category
                      ? {
                          label: formData.sub_category,
                          value: formData.sub_category
                        }
                      : null
                  }
                  onCreateOption={async (inputValue) => {
                    try {
                      const token = localStorage.getItem("token");
                      const selectedCategory = categories.find(c => c.name === formData.category);
                      if (!selectedCategory) {
                        alert("Please select Category first");
                        return;
                      }
                      await fetch(`${API}/api/master/sub-categories`, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          category_id: selectedCategory.id,
                          name: inputValue
                        })
                      });
                      await fetchSubCategories(selectedCategory.id);
                      setFormData(prev => ({ ...prev, sub_category: inputValue }));
                    } catch (err) {
                      console.error(err);
                      alert("Failed to create Sub Category");
                    }
                  }}
                  onChange={(selected) => {
                    setFormData(prev => ({
                      ...prev,
                      sub_category: selected?.value || ""
                    }));
                  }}
                />
              </div>

              {/* General Properties */}
              <div>
                <label style={s.label}>Material Variant Name</label>
                <div style={{ position: "relative" }}>
                  <Package size={16} style={{ position: "absolute", left: "12px", top: "12px", color: t.textMuted }} />
                  <input
                    type="text"
                    name="item_name"
                    value={formData.item_name}
                    onChange={handleChange}
                    placeholder="e.g. Commercial Grade Blockboard"
                    style={{ ...s.input, paddingLeft: "40px" }}
                  />
                </div>
              </div>

              <div>
                <label style={s.label}>Brand Line</label>
                <CreatableSelect
                  isMulti
                  placeholder="Add Brands"
                  value={(formData.brand || []).map(b => ({
                    label: b,
                    value: b
                  }))}
                  onChange={(selected) => {
                    setFormData(prev => ({
                      ...prev,
                      brand: selected ? selected.map(x => x.value) : []
                    }));
                  }}
                />
              </div>

              {/* Specification Attributes */}
              <div>
                <label style={s.label}>Thickness Dimension</label>
                <input
                  type="text"
                  name="thickness"
                  value={formData.thickness}
                  onChange={handleChange}
                  placeholder="e.g. 6mm, 12mm, 19mm"
                  style={s.input}
                />
              </div>

              <div>
                <label style={s.label}>Measurement UOM</label>
                <select name="uom" value={formData.uom} onChange={handleChange} style={s.input}>
                  <option value="">Select Matrix</option>
                  <option value="Nos">Nos</option>
                  <option value="Box">Box</option>
                  <option value="Feet">Feet</option>
                  <option value="Meter">Meter</option>
                  <option value="Sqft">Sqft</option>
                  <option value="Rft">Rft (Running Feet)</option>
                  <option value="Kg">Kg</option>
                  <option value="Ltr">Ltr</option>
                  <option value="NO">NO</option>
                  <option value="LS">LS</option>
                  <option value="ROLL">ROLL</option>
                </select>
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={s.label}>Specification / Remarks</label>
                <textarea
                  rows="2"
                  name="remarks"
                  value={formData.remarks}
                  onChange={handleChange}
                  placeholder=""
                  style={s.textarea}
                />
              </div>
            </div>
          </div>
        )}

        {/* DATA CONTAINER */}
        <div style={s.section}>
          <div style={s.sectionHead}>
            <div>
              <div style={{ fontSize: "16px", fontWeight: "700" }}>Material Registry Master Matrix</div>
              <div style={{ fontSize: "12px", color: t.textMuted, marginTop: "4px" }}>
                Live documentation lookup for interior space assets and procurement.
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <button onClick={handleExportCSV} style={s.secondaryBtn}>
                <Download size={15} /> Export Sheet (CSV)
              </button>
              <button onClick={handleExportPDF} style={s.secondaryBtn}>
                <Download size={15} /> Export Ledger (PDF)
              </button>
              <button
                onClick={() => document.getElementById("excelUpload").click()}
                style={s.secondaryBtn}
              >
                <Download size={15} /> Import Excel
              </button>
              <input
                id="excelUpload"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleExcelUpload}
              />
              <div style={s.searchBox}>
                <Search size={16} color={t.textMuted} />  
                <input
                  type="text"
                  placeholder="Filter by Name, Brand, Supplier, Code..."
                  value={tableSearch}
                  onChange={(e) => setTableSearch(e.target.value)}
                  style={s.searchInput}
                />
              </div>
            </div>
          </div>

          {/* QUICK DOMAIN FILTER PANEL */}
          <div style={s.tabContainer}>
            <button
              onClick={() => setSelectedCategory("All")}
              style={s.tabButton(selectedCategory === "All")}
            >
              All Matrices
            </button>
            {departments.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.name)}
                style={s.tabButton(selectedCategory === cat.name)}
              >
                {cat.name} Scope
              </button>
            ))}
          </div>

          {/* HIGH-DENSITY INTERIOR TABLE DISPLAY */}
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Code / Scope</th>
                  <th style={s.th}>Material Configuration Hierarchy</th>
                  <th style={s.th}>UOM</th>
                  <th style={s.th}>Remarks</th>
                  <th style={{ ...s.th, textAlign: "right" }}>System Operations</th>
                </tr>
              </thead>
              <tbody>
                {paginatedMaterials.length > 0 ? (
                  paginatedMaterials.map((item, index) => (
                    <tr key={index} style={{ "&:hover": { background: t.rowHover } }}>
                      <td style={s.td}>
                        <div style={{ fontFamily: "monospace", fontWeight: "700", marginBottom: "4px" }}>
                          {item.material_code || "UNASSIGNED"}
                        </div>
                        <span style={s.chip}>{item.department || "Interior"}</span>
                      </td>
                      <td style={s.td}>
                        <div style={{ fontWeight: "700", fontSize: "14.5px", color: t.text }}>{item.item_name}</div>
                        {item.category && (
                          <div style={{ fontSize: "12px", color: t.textMuted, marginTop: "2px", fontWeight: "500" }}>
                            {item.category} &rarr; <span style={{ color: t.text }}>{item.sub_category || "General"}</span>
                          </div>
                        )}
                        {(item.thickness || item.brand) && (
                          <div style={{ fontSize: "11.5px", color: t.textMuted, marginTop: "4px", background: "#f8fafc", padding: "4px 8px", borderRadius: "6px", display: "inline-block", border: `1px solid ${t.border}` }}>
                            {[
                              item.brand ? `Brand: ${item.brand}` : null,
                              item.thickness ? `Thk: ${item.thickness}` : null
                            ].filter(Boolean).join(" | ")}
                          </div>
                        )}
                        {item.remarks && (
                          <div>Remarks: {item.remarks}</div>
                        )}
                      </td>
                      <td style={s.td}>{item.uom || "-"}</td>
                      <td style={s.td}>{item.remarks || "-"}</td>
                      <td style={{ ...s.td, textAlign: "right" }}>
                        <div style={{ display: "inline-flex", gap: "4px" }}>
                          {!isPurchaseUser() && (
                            <button
                              title="Edit Record Parameters"
                              onClick={() => handleEdit(item)}
                              style={s.actionBtn(false)}
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                          {!isPurchaseUser() && (
                            <button
                              title="Purge Matrix Node"
                              onClick={() => handleDelete(item)}
                              style={s.actionBtn(true)}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="5"
                      style={{
                        ...s.td,
                        textAlign: "center",
                        color: t.textMuted,
                        padding: "48px 32px",
                      }}
                    >
                      No materials matched your organizational query criteria or workspace filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* FOOTER PAGINATION */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 24px",
              borderTop: `1px solid ${t.borderSoft}`,
            }}
          >
            <div style={{ fontSize: "12px", color: t.textMuted }}>
              Displaying <strong style={{ color: t.text }}>{paginatedMaterials.length}</strong> of <strong style={{ color: t.text }}>{filteredMaterials.length}</strong> architectural variants listed
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                style={s.primaryBtn}
              >
                <ChevronLeft size={14} />
              </button>

              <div style={{ fontSize: "13px", fontWeight: "600" }}>
                {currentPage} / {totalPages || 1}
              </div>

              <button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
                style={s.primaryBtn}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaterialMaster;